import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  getRatings,
  RatingWithReview,
} from '../api/marketplaceApi';
import { MarketplaceTargetType } from 'types/marketplace';

const PAGE_SIZE = 20;

export interface UseRatingsResult {
  ratings: RatingWithReview[];
  total: number;
  userRating: RatingWithReview | null;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

export function useRatings(
  targetType: MarketplaceTargetType | null,
  targetId: string | null
): UseRatingsResult {
  const apiClients = useApiClientsOrNull();

  const [ratings, setRatings] = useState<RatingWithReview[]>([]);
  const [total, setTotal] = useState(0);
  const [userRating, setUserRating] = useState<RatingWithReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setOffset(0);
    setRatings([]);
    setFetchTrigger((prev) => prev + 1);
  }, []);

  const loadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (!apiClients?.schemaClient || !targetType || !targetId) {
      return;
    }

    let cancelled = false;

    const fetchRatings = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getRatings(
          apiClients.schemaClient,
          targetType,
          targetId,
          { limit: PAGE_SIZE, offset }
        );

        if (!cancelled) {
          if (offset === 0) {
            setRatings(response.ratings);
          } else {
            setRatings((prev) => [...prev, ...response.ratings]);
          }
          setTotal(response.total);
          setUserRating(response.userRating);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load ratings';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchRatings();

    return () => {
      cancelled = true;
    };
  }, [apiClients, targetType, targetId, offset, fetchTrigger]);

  // Reset when target changes
  useEffect(() => {
    setOffset(0);
    setRatings([]);
    setUserRating(null);
    setTotal(0);
  }, [targetType, targetId]);

  const hasMore = ratings.length < total;

  return { ratings, total, userRating, loading, error, hasMore, loadMore, refetch };
}
